"use client";

import { useMemo } from "react";
import { isAddress } from "viem";
import { useAccount, useReadContract } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";

/**
 * Outcome of validating a candidate "parent" address -- where the buyer sits
 * in the binary tree, distinct from the "referrer" (who invited them).
 *
 * The rule is taken verbatim from the contract so the UI can never disagree
 * with what the chain will actually do. `Users._validatePlacementParent`
 * rejects a parent for exactly two reasons:
 *   - `ParentNotPlaced`         → `!_isInGenealogy(parent)`
 *   - `ParentCapacityReached`   → `parent.bv.dir >= 2`
 * Everything else is a client-side pre-check (format / self-parent).
 *
 * "In genealogy" is `exists && (id == 1 || depth > 0)`, NOT `exists` alone.
 * V2 introduced a state V1 did not have: `_createUnplacedUser` registers a
 * buyer before placing them, so a user can exist with `depth == 0` and still be
 * an invalid parent. Checking only `exists` would wave those through and the
 * revert would not surface until the user signs. The same expression is
 * correct on V1, where only the root has depth 0.
 *
 * This is deliberately a SEPARATE check from {@link useReferrerFieldValidation}:
 * a referrer only needs to exist in the genealogy (they are not accepting a
 * new direct child, just being credited), while a parent additionally needs a
 * free slot. Collapsing the two would incorrectly block a perfectly valid
 * referrer whose own tree happens to be full.
 */
export type ParentFieldStatus =
  | "idle" // nothing typed yet — show no feedback
  | "invalid" // not a well-formed 0x address
  | "self" // the buyer's own wallet
  | "checking" // chain read in flight
  | "not-member" // not in the genealogy: never placed in the tree
  | "full" // bv.dir >= 2: both legs already taken
  | "unreachable" // RPC failed — we genuinely do not know
  | "valid";

/** `Users.sol` allows at most two direct children per node. */
export const MAX_DIRECT_CHILDREN = 2n;

/** The tree root is the one placed node legitimately at depth 0. */
const ROOT_USER_ID = 1n;

/**
 * Status → key under the `checkout.parent` message namespace. Shared so the
 * inline field and the ticket page cannot drift apart in what they tell users.
 */
export const PARENT_MESSAGE_KEY: Record<ParentFieldStatus, string> = {
  idle: "requiredBelowThreshold",
  invalid: "invalid",
  self: "self",
  checking: "checking",
  "not-member": "notMember",
  full: "full",
  unreachable: "unreachable",
  valid: "valid",
};

export interface UseParentFieldValidationReturn {
  status: ParentFieldStatus;
  /** True only when the chain confirms this address can accept a new child. */
  isValid: boolean;
  refetch: () => void;
}

/**
 * Live-validates a candidate parent wallet address against the DAO contract.
 *
 * Deliberately reads the chain rather than the database: the whole class of
 * misplacement bugs this replaces came from trusting off-chain state that had
 * drifted from (or was never bound to) the wallet actually signing the mint.
 *
 * No manual debounce is needed — the read only fires once the input parses as a
 * complete address, and wagmi/react-query dedupes and caches per address.
 */
export const useParentFieldValidation = (
  rawAddress: string | undefined,
): UseParentFieldValidationReturn => {
  const { address: buyer } = useAccount();
  const candidate = (rawAddress ?? "").trim();

  const isWellFormed = isAddress(candidate);
  const isSelf =
    isWellFormed && !!buyer && candidate.toLowerCase() === buyer.toLowerCase();

  const parent = useReadContract({
    address: ContractAddress.GATEWAY,
    abi: GATEWAY_ABI,
    functionName: "getUser",
    args: [candidate as `0x${string}`],
    query: { enabled: isWellFormed && !isSelf },
  });

  const status = useMemo<ParentFieldStatus>(() => {
    if (candidate.length === 0) return "idle";
    if (!isWellFormed) return "invalid";
    if (isSelf) return "self";
    if (parent.isLoading) return "checking";
    if (parent.isError) return "unreachable";
    const user = parent.data;
    // `enabled` just flipped true and the first read has not resolved yet.
    if (!user) return "checking";
    // Mirrors Users._isInGenealogy. `exists` alone is not enough on V2 — see
    // the note on ParentFieldStatus.
    if (!user.exists || (user.id !== ROOT_USER_ID && user.depth === 0n)) {
      return "not-member";
    }
    if (user.bv.dir >= MAX_DIRECT_CHILDREN) return "full";
    return "valid";
  }, [
    candidate,
    isWellFormed,
    isSelf,
    parent.isLoading,
    parent.isError,
    parent.data,
  ]);

  return {
    status,
    isValid: status === "valid",
    refetch: parent.refetch,
  };
};
